/** PAGINA «MACHETĂ» a unui rând de structură: macheta unificată
 *  (`viewer/macheta`) pe planul de situație și arhitectura din amontele
 *  rândului, cu modelul complexului lui (`steps/model_fem/complex`) ca strat
 *  de structură. Drum: Studio → butonul «Machetă» din colțul vederii. */
import { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import * as api from "../api";
import { listSolutions } from "../nav/api";
import { useT } from "../i18n";
import { laOrigineLocala, northUp, origineDesen, regimulArhitecturii, toSiteLayer, toStoreyPlans } from "../viewer/canonical";
import type { CanonicalDoc, StoreyPlan } from "../viewer/canonical";
import type { SiteLayer } from "../viewer/client";
import { scenaSit } from "../viewer/plan/sit3d";
import MachetaUnificata from "../viewer/macheta/MachetaUnificata";
import { useModelComplex } from "../structure/modelComplex";

type Documente = { site: SiteLayer; lot: number[][]; niveluri: StoreyPlan[]; docArh: CanonicalDoc | null; origine: [number, number];
                   corpuri: { cod: string; spatiu: string; ansamblu?: boolean }[] };

export default function Macheta() {
  const { projectId = "", sol = "" } = useParams();
  const t = useT();
  const [doc, setDoc] = useState<Documente | null>(null);
  const [eroare, setEroare] = useState<string | null>(null);
  const { model, stare } = useModelComplex(projectId, sol, !!sol);

  useEffect(() => {
    if (!projectId || !sol) return;
    let viu = true;
    setDoc(null); setEroare(null);
    listSolutions(projectId).then(async (reg) => {
      const rand = reg.solutii.find((r) => r.id === sol);
      const sitId = rand?.lineage?.site_plan ?? null;
      const arhId = rand?.lineage?.architecture ?? null;
      if (!sitId) { if (viu) setEroare(t("macheta.fara_situatie")); return; }
      // ACEEAȘI compunere ca în Vizualizator: originea e a situației, iar
      // arhitectura e adusă la ea; corpurile iau regimul din arhitectură.
      const [sitBrut, arhBrut] = await Promise.all([
        api.solutionDocument(projectId, sitId),
        arhId ? api.solutionDocument(projectId, arhId, { parte: "macheta" }) : Promise.resolve(null),
      ]);
      if (!viu) return;
      const sit = northUp(sitBrut as unknown as CanonicalDoc);
      const origine = origineDesen(sit);
      const arh = arhBrut ? northUp(arhBrut as unknown as CanonicalDoc) : null;
      const arhLocal = arh ? laOrigineLocala(arh, origine) : null;
      const site0 = toSiteLayer(laOrigineLocala(sit, origine));
      const site = arhLocal ? regimulArhitecturii(site0, arhLocal) : site0;
      if (!site) { setEroare(t("macheta.fara_situatie")); return; }
      const niveluri = arhLocal ? toStoreyPlans(arhLocal) : [];
      setDoc({ site, lot: (site.boundary ?? []) as number[][], niveluri, docArh: arh, origine,
               corpuri: (rand?.structura?.corpuri ?? []).map((c) => ({ cod: c.cod, spatiu: c.spatiu, ansamblu: c.ansamblu })) });
    }).catch((e: unknown) => { if (viu) setEroare(e instanceof api.ApiError ? e.message : String(e)); });
    return () => { viu = false; };
  }, [projectId, sol, t]);

  const scena = useMemo(() => (doc ? scenaSit(doc.site, doc.lot as never, doc.niveluri) : null), [doc]);

  if (eroare) return <div className="sg-error">{eroare}</div>;
  if (!doc || !scena) return <div className="sg-muted" style={{ padding: 24 }}>{t("macheta.se_incarca")}</div>;
  return (
    <MachetaUnificata projectId={projectId} scena={scena} docArh={doc.docArh} planuri={doc.niveluri}
                      structura={stare === "gata" ? model : null} origine={doc.origine}
                      solutie={sol} corpuri={doc.corpuri} inapoi={`/p/${projectId}/structura/${sol}`} />
  );
}
